import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./AddFormField.css";

const FormField = () => {
  const [fields, setFields] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();
    fetchFields();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/form-modules/`);
      setModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form modules:", error);
    }
  };

  const fetchFields = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/form-fields/`);
      setFields(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form fields:", error);
      Swal.fire("Error", "Failed to load form fields", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This form field will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/form-fields/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Form field has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchFields();
    } catch (error) {
      console.error("Error deleting form field:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete form field",
        "error"
      );
    }
  };

  const handleEdit = (item) => {
    navigate(`/catalog/edit-form-field/${item.id}`);
  };

  const moduleLabel = (moduleId) => {
    const match = modules.find((m) => m.id === moduleId);
    return match ? match.module_name : "-";
  };

  const filteredFields = fields.filter((item) => {
    const search = searchTerm.toLowerCase();

    return (
      item.field_label?.toLowerCase().includes(search) ||
      item.field_code?.toLowerCase().includes(search) ||
      item.data_type?.toLowerCase().includes(search) ||
      moduleLabel(item.form_module).toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(
    filteredFields.length / entriesPerPage
  );

  const startIndex = (currentPage - 1) * entriesPerPage;

  const paginatedFields = filteredFields.slice(
    startIndex,
    startIndex + entriesPerPage
  );

  const startEntry =
    filteredFields.length === 0 ? 0 : startIndex + 1;

  const endEntry = Math.min(
    startIndex + entriesPerPage,
    filteredFields.length
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="form-field-table-page">
          <div className="form-field-table-header">
            <div className="form-field-table-header-left">
              <h2>
              
                Form Fields Management
              </h2>

              <span className="form-field-table-count">
                {fields.length} fields
              </span>
            </div>

            <div className="form-field-table-header-right">
              <div className="form-field-table-search-wrapper">
                <FiSearch className="form-field-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-field-table-search-input"
                />
              </div>

              <button
                className="form-field-table-refresh-btn"
                onClick={fetchFields}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>

              <button
                className="form-field-table-add-btn"
                onClick={() => navigate("/catalog/add-form-field")}
              >
                <FiPlus />
                Add Field
              </button>
            </div>
          </div>

          {loading ? (
            <div className="form-field-table-loader-container">
              <div className="form-field-table-loader">
                <div className="form-field-table-spinner"></div>
                <p>Loading form fields...</p>
              </div>
            </div>
          ) : (
            <div className="form-field-table-container">
              <table className="form-field-table">
                <thead>
                  <tr>
                    <th>Field Label</th>
                    <th>Field Code</th>
                    <th>Data Type</th>
                    <th>Form Module</th>
                    <th>Required</th>
                    <th>Status</th>
                    <th className="form-field-table-actions-header">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedFields.length > 0 ? (
                    paginatedFields.map((item) => (
                      <tr key={item.id}>
                        <td className="form-field-table-name">
                          <div className="form-field-table-name-cell">
                            <div className="form-field-table-avatar">
                              {item.field_label
                                ?.charAt(0)
                                .toUpperCase()}
                            </div>

                            <span>{item.field_label}</span>
                          </div>
                        </td>

                        <td>
                          <span className="form-field-table-code-badge">
                            {item.field_code}
                          </span>
                        </td>

                        <td>{item.data_type || "-"}</td>

                        <td>{moduleLabel(item.form_module)}</td>

                      <td>
  <span
    className={`form-field-table-requirement ${
      item.is_required
        ? "form-field-table-required"
        : "form-field-table-optional"
    }`}
  >
    <span className="form-field-table-requirement-dot"></span>
    {item.is_required ? "Required" : "Optional"}
  </span>
</td>
                        <td>
                          <span
                            className={`form-field-table-status ${
                              item.is_active
                                ? "form-field-table-active"
                                : "form-field-table-inactive"
                            }`}
                          >
                            <span className="form-field-table-status-dot"></span>
                            {item.is_active ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </td>

                        <td className="form-field-table-actions-cell">
                          <button
                            className="form-field-table-edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            className="form-field-table-delete-btn"
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="form-field-table-no-data"
                      >
                        <div className="form-field-table-empty-state">
                          <span className="form-field-table-empty-icon">
                            📋
                          </span>

                          <p>No form fields found</p>

                          <span className="form-field-table-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "Add your first form field to get started"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {filteredFields.length > 0 && (
                <div className="form-field-table-pagination">
                  <div className="form-field-table-pagination-info">
                    Showing <strong>{startEntry}</strong> to{" "}
                    <strong>{endEntry}</strong> of{" "}
                    <strong>{filteredFields.length}</strong> entries
                  </div>

                  <div className="form-field-table-pagination-controls">
                     <span>Show</span>
                    <select
                      className="form-field-table-entries-select"
                      value={entriesPerPage}
                      onChange={(e) =>
                        setEntriesPerPage(Number(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>

                    <button
                      className="form-field-table-page-btn form-field-table-prev-next"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.max(prev - 1, 1)
                        )
                      }
                      disabled={currentPage === 1}
                      title="Previous"
                    >
                      <FiChevronLeft />
                    </button>

                    <div className="form-field-table-page-numbers">
                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="form-field-table-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`form-field-table-page-btn ${
                              currentPage === page
                                ? "form-field-table-page-active"
                                : ""
                            }`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="form-field-table-page-btn form-field-table-prev-next"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, totalPages)
                        )
                      }
                      disabled={
                        currentPage === totalPages ||
                        totalPages === 0
                      }
                      title="Next"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormField;